import React, { useEffect, useRef, useState } from 'react';
import { Button, Col, Form, Modal, Row, Spinner } from 'react-bootstrap';
import { Table, Thead, Tbody, Tr, Th, Td } from 'react-super-responsive-table';
import 'react-super-responsive-table/dist/SuperResponsiveTableStyle.css';
import { Formik } from 'formik';
import MaskedInput from 'react-text-mask';
import moment from 'moment';
import Header from '~/Components/Header/Header';
import { toast } from 'react-toastify';
import { FaCheck, FaSearch, FaTimes } from 'react-icons/fa';
import * as api from '~/Services/Service';
import * as constants from '~/constant';
import { caracteristicas } from '~/constant';
import AuthStorage from '~/Utils/AuthStorage';

const SITUACAO_PENDENTE = 1;

const filtroInicial = {
  codigo_prospecto: '',
  data_inicio: '',
  data_fim: '',
};

const formatarData = data => {
  if (!data) return '-';
  const d = new Date(data);
  return (
    d.toLocaleDateString('pt-BR') +
    ' ' +
    d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })
  );
};

const dataBrParaIso = data => {
  if (!data) return null;
  const m = moment(data, 'DD/MM/YYYY', true);
  return m.isValid() ? m.format('YYYY-MM-DD') : null;
};

const ReservasPendentes = () => {
  const podeAprovar = AuthStorage.temCaracteristica(
    caracteristicas.AprovaVendaPendenciaJuridica
  );
  const [pendencias, setPendencias] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pendenciaSelecionada, setPendenciaSelecionada] = useState(null);
  const [mostrarModalAprovar, setMostrarModalAprovar] = useState(false);
  const [mostrarModalReprovar, setMostrarModalReprovar] = useState(false);
  const [motivo, setMotivo] = useState('');
  const [processando, setProcessando] = useState(false);
  const formikRef = useRef(null);

  const carregar = async values => {
    setLoading(true);
    try {
      const filtros = values || filtroInicial;
      const dados = await api.listarReservasPendentes({
        situacao: SITUACAO_PENDENTE,
        codigo_prospecto: filtros.codigo_prospecto
          ? parseInt(filtros.codigo_prospecto, 10)
          : null,
        data_inicio: dataBrParaIso(filtros.data_inicio),
        data_fim: dataBrParaIso(filtros.data_fim),
      });
      setPendencias(dados || []);
    } catch (err) {
      toast.error(err.message || 'Erro ao carregar pendências.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    carregar(filtroInicial);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const recarregarComFiltrosAtuais = () => {
    const values =
      formikRef.current && formikRef.current.values
        ? formikRef.current.values
        : filtroInicial;
    carregar(values);
  };

  const abrirModalAprovar = item => {
    setPendenciaSelecionada(item);
    setMotivo('');
    setMostrarModalAprovar(true);
  };

  const abrirModalReprovar = item => {
    setPendenciaSelecionada(item);
    setMotivo('');
    setMostrarModalReprovar(true);
  };

  const confirmarAprovacao = async () => {
    if (!pendenciaSelecionada) return;
    setProcessando(true);
    try {
      await api.aprovarReservaPendente(pendenciaSelecionada.codigo, motivo);
      toast.success('Solicitação aprovada.');
      setMostrarModalAprovar(false);
      recarregarComFiltrosAtuais();
    } catch (err) {
      toast.error(err.message || 'Erro ao aprovar solicitação.');
    } finally {
      setProcessando(false);
    }
  };

  const confirmarReprovacao = async () => {
    if (!pendenciaSelecionada) return;
    if (!motivo || !motivo.trim()) {
      toast.warning('Informe o motivo da reprovação.');
      return;
    }
    setProcessando(true);
    try {
      await api.reprovarReservaPendente(pendenciaSelecionada.codigo, motivo);
      toast.success('Solicitação reprovada.');
      setMostrarModalReprovar(false);
      recarregarComFiltrosAtuais();
    } catch (err) {
      toast.error(err.message || 'Erro ao reprovar solicitação.');
    } finally {
      setProcessando(false);
    }
  };

  return (
    <div>
      <Header title="Autoriza Venda com Pendência Jurídica" />
      <Formik
        innerRef={formikRef}
        initialValues={filtroInicial}
        onSubmit={values => carregar(values)}
      >
        {({ handleSubmit, handleChange, setFieldValue, values, resetForm }) => (
          <Form
            onSubmit={handleSubmit}
            className="container-fluid bg-white p-3"
          >
            <Row>
              <Col xs={12} sm={6} md={3}>
                <Form.Group>
                  <Form.Label>Código do Prospecto</Form.Label>
                  <Form.Control
                    type="number"
                    min="0"
                    name="codigo_prospecto"
                    value={values.codigo_prospecto}
                    onChange={handleChange}
                    placeholder="Ex.: 12345"
                  />
                </Form.Group>
              </Col>
              <Col xs={12} sm={6} md={3}>
                <Form.Group>
                  <Form.Label>Data Inicial</Form.Label>
                  <MaskedInput
                    name="data_inicio"
                    type="tel"
                    guide={false}
                    className="form-control"
                    placeholder="Ex.: 01/01/2026"
                    mask={constants.MASK_DATE}
                    value={values.data_inicio}
                    onChange={e =>
                      setFieldValue('data_inicio', e.target.value)
                    }
                  />
                </Form.Group>
              </Col>
              <Col xs={12} sm={6} md={3}>
                <Form.Group>
                  <Form.Label>Data Final</Form.Label>
                  <MaskedInput
                    name="data_fim"
                    type="tel"
                    guide={false}
                    className="form-control"
                    placeholder="Ex.: 31/12/2026"
                    mask={constants.MASK_DATE}
                    value={values.data_fim}
                    onChange={e => setFieldValue('data_fim', e.target.value)}
                  />
                </Form.Group>
              </Col>
            </Row>
            <div className="d-flex justify-content-end align-items-center">
              <Button
                type="button"
                variant="outline-secondary"
                className="mr-2"
                disabled={loading}
                onClick={() => {
                  resetForm({ values: filtroInicial });
                  carregar(filtroInicial);
                }}
              >
                Limpar
              </Button>
              <Button type="submit" variant="primary" disabled={loading}>
                {loading ? (
                  <>
                    <Spinner
                      as="span"
                      animation="border"
                      size="sm"
                      role="status"
                      aria-hidden="true"
                      className="mr-2"
                    />
                    Buscando...
                  </>
                ) : (
                  <>
                    <FaSearch className="mr-2" />
                    Filtrar
                  </>
                )}
              </Button>
            </div>
          </Form>
        )}
      </Formik>

      {loading ? (
        <div className="container-fluid bg-white p-5 mt-3 text-center">
          <Spinner animation="border" />
        </div>
      ) : pendencias.length === 0 ? (
        <div className="container-fluid bg-white p-5 mt-3 text-center text-muted">
          Nenhuma pendência encontrada.
        </div>
      ) : (
        <>
          <div className="container-fluid bg-white p-3 mt-3">
            <Row>
              <Col>
                <span>
                  Total de {pendencias.length} pendência(s) encontrada(s).
                </span>
              </Col>
            </Row>
          </div>
          <div className="container-fluid bg-white table-responsive">
            <Table className="table table-striped table-bordered table-hover">
              <Thead>
                <Tr className="text-center">
                  <Th>Código</Th>
                  <Th>Prospecto</Th>
                  <Th>Empreendimento</Th>
                  <Th>Quadra/Lote</Th>
                  <Th>Corretor</Th>
                  <Th>Data Solicitação</Th>
                  <Th>Ações</Th>
                </Tr>
              </Thead>
              <Tbody className="text-center">
                {pendencias.map(item => (
                  <Tr key={item.codigo}>
                    <Td>{item.codigo}</Td>
                    <Td className="text-left">
                      {item.codigo_prospecto} - {item.nome_prospecto}
                    </Td>
                    <Td className="text-left">{item.nome_empreendimento}</Td>
                    <Td>
                      {item.quadra} / {item.lote}
                    </Td>
                    <Td className="text-left">
                      {item.codigo_usuario} - {item.nome_usuario}
                    </Td>
                    <Td>{formatarData(item.data_tentativa_reserva)}</Td>
                    <Td>
                      {podeAprovar && (
                        <Button
                          variant="success"
                          size="sm"
                          className="mr-2"
                          onClick={() => abrirModalAprovar(item)}
                        >
                          <FaCheck /> Aprovar
                        </Button>
                      )}
                      <Button
                        variant="danger"
                        size="sm"
                        onClick={() => abrirModalReprovar(item)}
                      >
                        <FaTimes /> Reprovar
                      </Button>
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </div>
        </>
      )}

      <Modal
        show={mostrarModalAprovar}
        onHide={() => setMostrarModalAprovar(false)}
        centered
      >
        <Modal.Header
          closeButton
          style={{
            background: '#d1e7dd',
            borderBottom: '1px solid #badbcc',
          }}
        >
          <Modal.Title
            style={{ color: '#0f5132', fontSize: '16px', fontWeight: 600 }}
          >
            Aprovar Solicitação
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {pendenciaSelecionada && (
            <>
              <p>
                <strong>Prospecto:</strong>{' '}
                {pendenciaSelecionada.nome_prospecto}
                <br />
                <strong>Empreendimento:</strong>{' '}
                {pendenciaSelecionada.nome_empreendimento}
                <br />
                <strong>Quadra/Lote:</strong> {pendenciaSelecionada.quadra} /{' '}
                {pendenciaSelecionada.lote}
              </p>
              <Form.Group>
                <Form.Label>Observação (opcional)</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  value={motivo}
                  onChange={e => setMotivo(e.target.value)}
                  maxLength={500}
                />
              </Form.Group>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setMostrarModalAprovar(false)}
          >
            Cancelar
          </Button>
          <Button
            variant="success"
            onClick={confirmarAprovacao}
            disabled={processando}
          >
            {processando ? 'Aprovando...' : 'Confirmar Aprovação'}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={mostrarModalReprovar}
        onHide={() => setMostrarModalReprovar(false)}
        centered
      >
        <Modal.Header
          closeButton
          style={{
            background: '#f8d7da',
            borderBottom: '1px solid #f5c2c7',
          }}
        >
          <Modal.Title
            style={{ color: '#842029', fontSize: '16px', fontWeight: 600 }}
          >
            Reprovar Solicitação
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {pendenciaSelecionada && (
            <>
              <p>
                <strong>Prospecto:</strong>{' '}
                {pendenciaSelecionada.nome_prospecto}
                <br />
                <strong>Empreendimento:</strong>{' '}
                {pendenciaSelecionada.nome_empreendimento}
                <br />
                <strong>Quadra/Lote:</strong> {pendenciaSelecionada.quadra} /{' '}
                {pendenciaSelecionada.lote}
              </p>
              <Form.Group>
                <Form.Label>Motivo da reprovação *</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  value={motivo}
                  onChange={e => setMotivo(e.target.value)}
                  maxLength={500}
                  required
                />
              </Form.Group>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setMostrarModalReprovar(false)}
          >
            Cancelar
          </Button>
          <Button
            variant="danger"
            onClick={confirmarReprovacao}
            disabled={processando || !motivo.trim()}
          >
            {processando ? 'Reprovando...' : 'Confirmar Reprovação'}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default ReservasPendentes;
